import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import { RootState } from '../rootReducer';
import AjaxUtil from '../../axios/axios';
import { set } from 'lodash';
import { selectTenantFeaturesMap } from './tenantSlice';

// interfaces
export interface AuthError {
    message: string
}

export interface ProjectPermissionData {
    projectId: string;
    permissionData: Array<object>;
}

export interface AuthState {
    isAuth: boolean
    currentUser?: CurrentUser,
    currentChat?: CurrentChat,
    projectPermissionData?: ProjectPermissionData[],
    isLoading: boolean;
    isTfaRequired: boolean;
    tfaMethod?: string;
    tempCredentials?: { username: string, password: string };
    error: AuthError;
}

export interface CurrentUser {
    EmailID: string
    CashbackAmount: string
    FullName: string
    MobileNumber: string
    PGCourse: string
    PackageID: string
    ReferralCode: string
    UserNumber: string
    UserStatus: string
    UserType: string
}

export interface CurrentChat {
    id: string,
    name: string,
    userdp: string
}

export const initialState: AuthState = {
    isAuth: false,
    isLoading: false,
    isTfaRequired: false,
    error: { message: '' },
    projectPermissionData: [],
}

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setLoading: (state, { payload }: PayloadAction<boolean>) => {
            state.isLoading = payload
        },
        setAuthSuccess: (state, { payload }: PayloadAction<CurrentUser>) => {
            state.currentUser = payload
            state.isAuth = true
            state.projectPermissionData = []
            state.isTfaRequired = false
        },
        setProjectData: (state, { payload }: PayloadAction<CurrentChat>) => {
            state.currentChat = payload
            state.isAuth = true
        },
        setProjectPermissionData: (state, { payload }: PayloadAction<ProjectPermissionData>) => {
            state.projectPermissionData?.push(payload)
        },
        removeProject: (state, { payload }: PayloadAction<string>) => {
            state.projectPermissionData = state.projectPermissionData && state.projectPermissionData.filter(
                (project) => project.projectId !== payload
            );
        },
        setLogOut: (state) => {
            state.isAuth = false
            state.currentUser = undefined
            state.isTfaRequired = false
        },
        setAuthFailed: (state, { payload }: PayloadAction<AuthError>) => {
            state.error = payload
            state.isAuth = false
        },
        setTfaRequired: (state, { payload }: PayloadAction<boolean>) => {
            state.isTfaRequired = payload;
        },
        setTempCredentials: (state, { payload }: PayloadAction<{ username: string, password: string } | undefined>) => {
            state.tempCredentials = payload;
        },
        setTfaMethod: (state, { payload }: PayloadAction<string | undefined>) => {
            state.tfaMethod = payload;
        },
    },
})
export const { setAuthSuccess, setLogOut, setLoading, setAuthFailed, setProjectPermissionData, removeProject, setTfaRequired, setTempCredentials, setTfaMethod } = authSlice.actions;

export const login = (userName, password): AppThunk => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const response = await AjaxUtil.sendRequest('twaotpgenration', { "username": userName, "password": AjaxUtil.encryptData(password) }, false, { isAuth: true, method: 'post', showError: true });

        const tfaMethod = response?.["2FA Method"];
        dispatch(setTfaMethod(tfaMethod));
        dispatch(setTempCredentials({ username: userName, password: AjaxUtil.encryptData(password) }));
        dispatch(setTfaRequired(true));
    } catch (error: any) {
        dispatch(setAuthFailed({ message: error?.response?.data?.message || "error" }));
    } finally {
        dispatch(setLoading(false));
    }
}

export const finalizeLoginWithOtp = (otp: string): AppThunk => async (dispatch, getState) => {
    try {
        const { tempCredentials } = (getState() as RootState).auth;
        if (!tempCredentials) {
            throw new Error("Missing login credentials");
        }

        dispatch(setLoading(true));
        // Call /login with username, password, and otp
        const response = await AjaxUtil.sendRequest('login', {
            "username": tempCredentials.username,
            "password": tempCredentials.password,
            "otp": otp
        }, false, { isAuth: true, method: 'post', showError: true });

        if (response && response.status !== "error") {
            const user = response.data || response;
            dispatch(setAuthSuccess(user));
            dispatch(setTfaRequired(false));
            dispatch(setTempCredentials(undefined));
            dispatch(addAiSubscription());
            dispatch(startRefreshTokenTimer());
        } else {
            dispatch(setAuthFailed({ message: response?.message || "Invalid OTP" }));
        }
    } catch (error: any) {
        dispatch(setAuthFailed({ message: error?.response?.data?.message || "error" }));
    } finally {
        dispatch(setLoading(false));
    }
}

export const finalizeLogin = (userData: CurrentUser): AppThunk => async (dispatch) => {
    dispatch(setAuthSuccess(userData));
    dispatch(setTfaRequired(false));
    dispatch(addAiSubscription());
    dispatch(startRefreshTokenTimer());
}

// Logout thunk unchanged, but clear localStorage in logout thunk:

export const logOut_thunk = (reason: string = "USER_LOGOUT"): AppThunk => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        try {
            await AjaxUtil.sendRequest('api/v1/user/devices/logout', { "reason": reason }, false, { isAuth: true, method: 'post', isLogout: true });
        } catch (apiError) {
            console.error("Logout API failed", apiError);
        }
        dispatch(setLogOut());
        localStorage.removeItem('coguide_token');
        localStorage.removeItem('coguide_expire');
        localStorage.removeItem("coguide_user");
    } catch (error) {
        dispatch(setAuthFailed({ message: "error" }));
    } finally {
        dispatch(setLoading(false));
    }
}

// New refreshToken thunk:

export const refreshTokenThunk = (): AppThunk => async (dispatch) => {
    try {
        const response = await AjaxUtil.sendRequest('refresh_token', {}, false, { method: 'get', showError: true });
        localStorage.setItem('coguide_token', response['token']);
        localStorage.setItem('coguide_expire', response['expire']);
        console.log("inside regresh tokenat iuikl")
        dispatch(addAiSubscription())
        dispatch(startRefreshTokenTimer());
    } catch {
        dispatch(setLogOut());
        localStorage.removeItem('coguide_token');
        localStorage.removeItem('coguide_expire');
        localStorage.removeItem('coguide_user');
    }
};

export const addAiSubscription = (): AppThunk => async (dispatch) => {
    console.log("calling ai subscription");
    try {
        dispatch(setLoading(true));
        const response = await AjaxUtil.sendRequest('add/aisubscription', {}, false, { method: 'post', showError: true });
    } catch (error) {
        console.error(error);
    } finally {
        dispatch(setLoading(false));
    }
}

// Schedule refresh token call one minute before expiry
export const startRefreshTokenTimer = (): AppThunk => (dispatch) => {
    const expireDate = localStorage.getItem('coguide_expire');
    if (!expireDate) {
        dispatch(setLogOut());
        return;
    }
    const expires = new Date(expireDate);
    if (expires.getTime() < Date.now()) {
        dispatch(setLogOut());
        localStorage.removeItem('coguide_token');
        localStorage.removeItem('coguide_expire');
        localStorage.removeItem('coguide_user');
        return;
    }
    const timeout = expires.getTime() - Date.now() - 60000; // 1 minute before expiry
    setTimeout(() => {
        dispatch(refreshTokenThunk());
    }, timeout);
};

// Define basic feature toggle overrides using reselect to handle projectPermissionData
const selectAuthBase = (state: RootState) => state.auth;

export const authSelector = createSelector(
    [selectAuthBase, selectTenantFeaturesMap],
    (auth, tenantMap) => {
        // Only map if both are loaded and there's a feature map to verify against
        if (!auth.projectPermissionData || Object.keys(tenantMap).length === 0) {
            return auth;
        }

        const newPermissions = auth.projectPermissionData.map(project => {
            const newPermissionData = project.permissionData.map((group: any) => {
                const groupKey = Object.keys(group)[0];
                const groupItems = group[groupKey];
                
                if (!Array.isArray(groupItems)) return group;

                const newGroupItems = groupItems.map((item: any) => {
                    const actionName = item.action?.toLowerCase()?.replace(',', '')?.trim();
                    const isTenantAllowed = tenantMap[actionName]; 

                    // If the feature is explicitly forbidden at the tenant level, force it to be unselected.
                    // This protects existing app permissions from being wiped if they aren't explicitly listed in the tenant JSON.
                    if (isTenantAllowed === false) {
                        const modifiedPerms = item.permissions.map((p: any) => 
                            p.title === "allowed" ? { ...p, is_selected: false } : p
                        );
                        return { ...item, permissions: modifiedPerms };
                    }
                    return item;
                });

                return { [groupKey]: newGroupItems };
            });

            return { ...project, permissionData: newPermissionData };
        });

        return { ...auth, projectPermissionData: newPermissions };
    }
);
export default authSlice.reducer;


// https://blog.logrocket.com/using-typescript-with-redux-toolkit/